import UserAvatar20 from '@carbon/icons-react/es/user--avatar/20';
import { Col, Form, Row, Tooltip } from 'antd';
import React, { FC } from 'react';
import { Helmet } from 'react-helmet';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { HideForPartner } from 'components/atoms/user-type-utils';
import { Page } from 'components/molecules/page';
import { HeaderWithIconTitle } from 'components/molecules/page/page';
import { useIsSamlMandatory } from 'components/organisms/saml';
import { BodyShort01 } from 'components/styles';
import { useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { fullName } from 'utils/full-name';
import { SettingsLabel } from '../styles';
import { TeamMemberSectionField } from '../team-members/section-field/section-field';
import { ChangeNameSetting } from './change-name';
import { ChangePasswordSetting } from './change-password';
import { ChangeProfilePic } from './change-profile-pic';
import { TimeOffSetting } from './timeoff';

const PageContent = styled(Page.Content)`
  padding: var(--layout-03);

  > .ant-row {
    padding: var(--spacing-03);
  }
`;

export const StyledFormContent = styled(Form.Item)`
  margin: var(--layout-03) 0 0;
`;

const HelpText = styled.span`
  ${BodyShort01};
`;

const StyledDiv = styled.div`
  display: flex;
  align-items: center;
`;

const StyledCol = styled(Col)`
  width: 45%;
`;

const SectionFieldContainer = styled.div`
  margin-top: var(--layout-03);

  .ozone-select__multi-value__remove {
    display: none;
  }
`;

export const ProfileSettingsPage: FC = () => {
  const { user } = useUser();
  const title = useTranslation('profile');
  const isSamlMandate = useIsSamlMandatory();

  return (
    <Page>
      <Helmet>{title}</Helmet>
      <Page.Header
        $white
        sticky
        title={<HeaderWithIconTitle icon={<UserAvatar20 />} title={title} />}
      />
      <PageContent>
        <Row>
          <StyledCol>
            <ChangeProfilePic user={user} />

            <Form>
              <StyledFormContent>
                <SettingsLabel>
                  <FormattedMessage id="name" />
                </SettingsLabel>
                {fullName(user)}
                <br />
                <ChangeNameSetting />
              </StyledFormContent>

              <StyledFormContent>
                <SettingsLabel>
                  <FormattedMessage id="role" />
                </SettingsLabel>
                <StyledDiv>
                  <Tooltip
                    title={
                      <FormattedMessage id="settings.profile.role.help_text" />
                    }
                    placement="right"
                  >
                    <HelpText>{user.role?.roleName}</HelpText>
                  </Tooltip>
                </StyledDiv>
              </StyledFormContent>

              <HideForPartner>
                <StyledFormContent>
                  <SettingsLabel>
                    <FormattedMessage id="settings.profile.capacity.title" />
                  </SettingsLabel>
                  <StyledDiv>
                    <Tooltip
                      title={
                        <FormattedMessage id="settings.profile.capacity.help_text" />
                      }
                      placement="right"
                    >
                      <HelpText>
                        <FormattedMessage
                          id="settings.profile.capacity.content"
                          values={{ hours: user.capacity }}
                        />
                      </HelpText>
                    </Tooltip>
                  </StyledDiv>
                </StyledFormContent>
              </HideForPartner>

              {!isSamlMandate ? (
                <StyledFormContent>
                  <SettingsLabel>
                    <FormattedMessage id="password" />
                  </SettingsLabel>
                  ********
                  <br />
                  <ChangePasswordSetting />
                </StyledFormContent>
              ) : null}

              <HideForPartner>
                <StyledFormContent>
                  <SettingsLabel>
                    <FormattedMessage id="settings.profile.timeoff.title" />
                  </SettingsLabel>
                  <TimeOffSetting />
                </StyledFormContent>
              </HideForPartner>
            </Form>
          </StyledCol>
        </Row>
        <HideForPartner>
          <Row>
            <Col span="24">
              <SectionFieldContainer>
                <TeamMemberSectionField
                  readOnly
                  fields={user?.fields}
                  isProfileView
                />
              </SectionFieldContainer>
            </Col>
          </Row>
        </HideForPartner>
      </PageContent>
    </Page>
  );
};
